/** The revenue filter of Manage Learners: two Vietnam days, as the server takes them (YYYY-MM-DD). */
export interface RevenueDateRange {
	from: string
	to: string
}

export const EMPTY_REVENUE_RANGE: RevenueDateRange = { from: '', to: '' }

export type RevenueQuickPick =
	| 'today'
	| 'yesterday'
	| 'last_7_days'
	| 'last_14_days'
	| 'last_30_days'
	| 'this_week'
	| 'last_week'
	| 'this_month'
	| 'last_month'

/** The quick picks, in the order they are listed. */
export const REVENUE_QUICK_PICKS: { value: RevenueQuickPick; label: string }[] = [
	{ value: 'today', label: 'Today' },
	{ value: 'yesterday', label: 'Yesterday' },
	{ value: 'last_7_days', label: 'Last 7 days' },
	{ value: 'last_14_days', label: 'Last 14 days' },
	{ value: 'last_30_days', label: 'Last 30 days' },
	{ value: 'this_week', label: 'This week' },
	{ value: 'last_week', label: 'Last week' },
	{ value: 'this_month', label: 'This month' },
	{ value: 'last_month', label: 'Last month' },
]

const DAY_MS = 24 * 60 * 60 * 1000
// Vietnam is UTC+7 all year.
const VIETNAM_OFFSET_MS = 7 * 60 * 60 * 1000

/**
 * A Vietnam day is kept as the UTC midnight that carries the same year, month
 * and day, so none of the math below reads the browser's timezone.
 */
const vietnamDay = (now: Date) => Math.floor((now.getTime() + VIETNAM_OFFSET_MS) / DAY_MS) * DAY_MS

const dayText = (day: number) => new Date(day).toISOString().slice(0, 10)

/** The day it is in Vietnam at `now`, whatever the browser's timezone. */
export const vietnamToday = (now: Date) => dayText(vietnamDay(now))

/**
 * The range of a quick pick at `now`. "Last N days" counts today, a week starts
 * on Monday, and "This week" and "This month" end today, not in the future.
 */
export const quickPickRange = (pick: RevenueQuickPick, now: Date): RevenueDateRange => {
	const today = vietnamDay(now)
	const date = new Date(today)
	const range = (from: number, to: number) => ({ from: dayText(from), to: dayText(to) })
	// getUTCDay is 0 on Sunday; a Sunday is the seventh day of its week.
	const monday = today - ((date.getUTCDay() + 6) % 7) * DAY_MS
	const monthStart = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)
	switch (pick) {
		case 'today':
			return range(today, today)
		case 'yesterday':
			return range(today - DAY_MS, today - DAY_MS)
		case 'last_7_days':
			return range(today - 6 * DAY_MS, today)
		case 'last_14_days':
			return range(today - 13 * DAY_MS, today)
		case 'last_30_days':
			return range(today - 29 * DAY_MS, today)
		case 'this_week':
			return range(monday, today)
		case 'last_week':
			return range(monday - 7 * DAY_MS, monday - DAY_MS)
		case 'this_month':
			return range(monthStart, today)
		case 'last_month':
			return range(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - 1, 1), monthStart - DAY_MS)
		default:
			return EMPTY_REVENUE_RANGE
	}
}

/** The quick pick that gives exactly this range at `now`, to mark it in the list. */
export const matchingQuickPick = (range: RevenueDateRange, now: Date): RevenueQuickPick | null => {
	if (!range.from || !range.to) return null
	const match = REVENUE_QUICK_PICKS.find(({ value }) => {
		const picked = quickPickRange(value, now)
		return picked.from === range.from && picked.to === range.to
	})
	return match ? match.value : null
}

const DAY_TEXT = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * The calendar works with browser dates. A Vietnam day is shown on it as the
 * same year, month and day at the browser's midnight, so the day staff click is
 * the day that is sent, in any timezone.
 */
export const calendarDate = (day: string): Date | null => {
	const parts = DAY_TEXT.exec(day)
	return parts ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])) : null
}

const twoDigits = (value: number) => String(value).padStart(2, '0')

/** The day a calendar date stands for, read back the way calendarDate wrote it. */
export const calendarDayText = (date: Date) =>
	`${date.getFullYear()}-${twoDigits(date.getMonth() + 1)}-${twoDigits(date.getDate())}`

const displayDay = (day: string) => {
	const parts = DAY_TEXT.exec(day)
	return parts ? `${parts[3]}/${parts[2]}/${parts[1]}` : day
}

/** What the field shows: 28/09/2026 - 04/10/2026, one date for a single day, nothing with no filter. */
export const revenueRangeText = (range: RevenueDateRange) => {
	if (!range.from && !range.to) return ''
	if (!range.to) return `From ${displayDay(range.from)}`
	if (!range.from) return `Until ${displayDay(range.to)}`
	return range.from === range.to
		? displayDay(range.from)
		: `${displayDay(range.from)} - ${displayDay(range.to)}`
}
